/**
 * IDE layout model (M2C.1, v3): which panels are visible, where Files / Agent / History sit, and whether History is its
 * own panel. It is the SOURCE OF TRUTH for the arrangement; panel sizes stay with react-resizable-panels (persisted per
 * arrangement). Everything stored is validated; anything unexpected falls back to the canonical default.
 *
 * Panels: Files, Code, Tools (terminal & co.), Agent, History. Code and Tools are INDEPENDENT: any combination is a
 * valid layout (Code + Tools, Code only, Tools only, neither). When neither is visible the whole central column
 * disappears and the remaining panels share the width. History is embedded in the Agent panel by default; when it is
 * shown as its own panel it occupies a left or right slot independently of Agent.
 *
 * Docking is deliberately constrained (left/right slots around the central column), not a free-form docking engine:
 * that keeps Monaco, xterm and the chat controller stable (see ARCHITECTURE.md §9.2).
 */
export const LAYOUT_MODEL_VERSION = 3;
export const LAYOUT_MODEL_KEY = "racler.layout.v3:model";
/** The M2A.6 model (v2) lived here. It is still READ (and migrated) but never written. */
export const LEGACY_LAYOUT_MODEL_KEY = "racler.layout.v1:model";

export type Side = "left" | "right";
export const PANELS = ["files", "code", "tools", "agent"] as const;
export type PanelName = (typeof PANELS)[number];
export const DOCKABLE = ["files", "history", "agent"] as const;
export type Dockable = (typeof DOCKABLE)[number];

export interface LayoutModel {
  v: typeof LAYOUT_MODEL_VERSION;
  visible: Record<PanelName, boolean>;
  /** History as its own panel (`standalone`) on `side`. Not standalone = embedded in the Agent panel (default). */
  history: { standalone: boolean; side: Side };
  dock: { files: Side; agent: Side };
}

export const DEFAULT_LAYOUT: LayoutModel = {
  v: LAYOUT_MODEL_VERSION,
  visible: { files: true, code: true, tools: true, agent: true },
  history: { standalone: false, side: "left" },
  dock: { files: "left", agent: "right" },
};

const isSide = (x: unknown): x is Side => x === "left" || x === "right";
const isObj = (x: unknown): x is Record<string, unknown> =>
  typeof x === "object" && x !== null && !Array.isArray(x);
const opposite = (s: Side): Side => (s === "left" ? "right" : "left");

function parseV3(x: Record<string, unknown>): LayoutModel | null {
  const { visible, history, dock } = x;
  if (!isObj(visible) || !isObj(history) || !isObj(dock)) return null;
  for (const p of PANELS) if (typeof visible[p] !== "boolean") return null;
  if (typeof history["standalone"] !== "boolean" || !isSide(history["side"])) return null;
  if (!isSide(dock["files"]) || !isSide(dock["agent"])) return null;
  return {
    v: LAYOUT_MODEL_VERSION,
    visible: {
      files: visible["files"] as boolean,
      code: visible["code"] as boolean,
      tools: visible["tools"] as boolean,
      agent: visible["agent"] as boolean,
    },
    history: { standalone: history["standalone"], side: history["side"] },
    dock: { files: dock["files"], agent: dock["agent"] },
  };
}

/**
 * M2A.6 (v2) → v3. v2 had no stored visibility (it lived in the panel library's sizes, which are not carried over:
 * everything starts visible) and "detached History" sat above the Agent; it becomes a standalone History panel on the
 * side opposite the Agent.
 */
function migrateV2(x: Record<string, unknown>): LayoutModel | null {
  const dock = x["dock"];
  if (
    typeof x["historyDetached"] !== "boolean" ||
    !isObj(dock) ||
    !isSide(dock["files"]) ||
    !isSide(dock["agent"])
  ) {
    return null;
  }
  return {
    ...DEFAULT_LAYOUT,
    history: { standalone: x["historyDetached"], side: opposite(dock["agent"]) },
    dock: { files: dock["files"], agent: dock["agent"] },
  };
}

/** Parses stored JSON; returns the default for anything that is not a valid v3 model or a migratable v2 model. */
export function parseLayoutModel(raw: string | null | undefined): LayoutModel {
  if (!raw) return DEFAULT_LAYOUT;
  try {
    const x: unknown = JSON.parse(raw);
    if (!isObj(x)) return DEFAULT_LAYOUT;
    if (x["v"] === LAYOUT_MODEL_VERSION) return parseV3(x) ?? DEFAULT_LAYOUT;
    if (x["v"] === 2) return migrateV2(x) ?? DEFAULT_LAYOUT;
    return DEFAULT_LAYOUT;
  } catch {
    return DEFAULT_LAYOUT;
  }
}

export const serializeLayoutModel = (m: LayoutModel): string => JSON.stringify(m);

export const isDefaultLayout = (m: LayoutModel): boolean =>
  serializeLayoutModel(m) === serializeLayoutModel(DEFAULT_LAYOUT);

// ---- arrangement -----------------------------------------------------------------------------------------------
export type Column = Dockable | "center";
export type CenterMode = "both" | "code" | "tools" | "none";

export interface Arrangement {
  /** Left-to-right order of the horizontal group. `center` is absent when neither Code nor Tools is visible. */
  columns: Column[];
  center: CenterMode;
}

const sideOf = (m: LayoutModel, p: Dockable): Side => (p === "history" ? m.history.side : m.dock[p]);
const shown = (m: LayoutModel, p: Dockable): boolean =>
  p === "history" ? m.history.standalone : m.visible[p];

export function centerMode(m: LayoutModel): CenterMode {
  if (m.visible.code && m.visible.tools) return "both";
  if (m.visible.code) return "code";
  if (m.visible.tools) return "tools";
  return "none";
}

/** Which columns exist, left to right. Within a side the order is Files, History, Agent. */
export function arrangement(m: LayoutModel): Arrangement {
  const center = centerMode(m);
  const left = DOCKABLE.filter((p) => shown(m, p) && sideOf(m, p) === "left");
  const right = DOCKABLE.filter((p) => shown(m, p) && sideOf(m, p) === "right");
  const columns: Column[] = [...left, ...(center === "none" ? [] : (["center"] as const)), ...right];
  return { columns, center };
}

/**
 * Panel sizes are stored per group id. A different arrangement has different panels, so it must use a different id;
 * otherwise stale sizes of another arrangement would be applied.
 */
export const arrangementKey = (m: LayoutModel): string => {
  const a = arrangement(m);
  return `${a.columns.join("-") || "empty"}:${a.center}`;
};

export const anyVisible = (m: LayoutModel): boolean => arrangement(m).columns.length > 0;

// ---- updates (pure) ----------------------------------------------------------------------------------------------
export const withVisible = (m: LayoutModel, panel: PanelName, visible: boolean): LayoutModel =>
  m.visible[panel] === visible ? m : { ...m, visible: { ...m.visible, [panel]: visible } };

export const toggled = (m: LayoutModel, panel: PanelName): LayoutModel =>
  withVisible(m, panel, !m.visible[panel]);

export const withDock = (m: LayoutModel, panel: "files" | "agent", side: Side): LayoutModel => ({
  ...m,
  dock: { ...m.dock, [panel]: side },
});

/** Showing History as its own panel puts it opposite the Agent unless a side was chosen before. */
export const withHistoryStandalone = (m: LayoutModel, standalone: boolean): LayoutModel => ({
  ...m,
  history: { ...m.history, standalone },
});
export const withHistorySide = (m: LayoutModel, side: Side): LayoutModel => ({
  ...m,
  history: { ...m.history, side },
});
/** Default slot for a freshly opened History panel: the side opposite the Agent. */
export const historyOpenedModel = (m: LayoutModel): LayoutModel => ({
  ...m,
  history: { standalone: true, side: opposite(m.dock.agent) },
});

// ---- storage ---------------------------------------------------------------------------------------------------
type ReadStore = Pick<Storage, "getItem">;
type WriteStore = Pick<Storage, "setItem">;

export const loadLayoutModel = (store: ReadStore | undefined): LayoutModel => {
  try {
    const current = store?.getItem(LAYOUT_MODEL_KEY);
    if (current) return parseLayoutModel(current);
    return parseLayoutModel(store?.getItem(LEGACY_LAYOUT_MODEL_KEY));
  } catch {
    return DEFAULT_LAYOUT;
  }
};
export const saveLayoutModel = (store: WriteStore | undefined, m: LayoutModel): void => {
  try {
    store?.setItem(LAYOUT_MODEL_KEY, serializeLayoutModel(m));
  } catch {
    // storage unavailable: the layout is simply not remembered
  }
};

/** Every key this layout system ever wrote (v1 library sizes + v2 model, v3 sizes + model). */
export const LAYOUT_KEY_PREFIXES = ["racler.layout.v1", "racler.layout.v3"] as const;
export const isLayoutKey = (key: string): boolean => LAYOUT_KEY_PREFIXES.some((p) => key.startsWith(p));

// ---- default sizes ---------------------------------------------------------------------------------------------
const BASE_SIZE: Record<Column, number> = { files: 18, history: 20, agent: 28, center: 0 };

/**
 * Initial sizes (percent, summing to exactly 100) for the columns of an arrangement. With a central column the side
 * panels keep their usual share and the centre takes the rest; without one the side panels share the whole width.
 */
export function defaultSizes(a: Arrangement): Record<string, number> {
  const side = a.columns.filter((c) => c !== "center");
  const out: Record<string, number> = {};
  if (a.columns.includes("center")) {
    for (const c of side) out[c] = BASE_SIZE[c];
    out["center"] = 100 - side.reduce((s, c) => s + BASE_SIZE[c], 0);
    return out;
  }
  const total = side.reduce((s, c) => s + BASE_SIZE[c], 0);
  let used = 0;
  side.forEach((c, i) => {
    const size =
      i === side.length - 1
        ? Math.round((100 - used) * 100) / 100
        : Math.round((BASE_SIZE[c] / total) * 10000) / 100;
    out[c] = size;
    used += size;
  });
  return out;
}
